import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';

import { isStaleSessionModalOpenVar } from 'lib/session-stale-monitor.apollo';
import {
  BASE_ACTIVITIES_PRESENT,
  PRIVATE_SIGNIN_REFRESH_SESSION,
} from 'global/private-routes.constants';
import { getStudentData } from 'approot/student-class-to-do/student-class-to-do.utils';
import { signInStateVar } from 'approot/shared/user/user-data.apollo';
import { useExtendSession } from 'approot/shared/signin/signin-refresh-session/use-extend-session';
import { isItTimeToShowSessionWarning } from './session/session-storage-duration';
import { getSessionToken } from './session/get-session-token';

const INTERVAL_IN_MILLISECONDS = 10000;

export const SessionStaleMonitor = () => {
  const location = useLocation();

  const isModalOpen = useReactiveVar(isStaleSessionModalOpenVar);
  const { isSignedIn, isStudentSignedIn } = useReactiveVar(signInStateVar);
  const isClassToDoStudent = getStudentData().token;

  // Timer should only run if the user is a signed in or an authenticated student
  // and the user is not on the refresh session page
  const shouldTimerBeRunning = useMemo(
    () =>
      (isSignedIn || (isStudentSignedIn && !isClassToDoStudent)) &&
      !location.pathname.includes(PRIVATE_SIGNIN_REFRESH_SESSION),
    [isSignedIn, isStudentSignedIn, location.pathname, isClassToDoStudent]
  );

  const { extendSession } = useExtendSession({
    token: getSessionToken(),
  });

  useEffect(() => {
    if (!shouldTimerBeRunning) {
      return;
    }

    // Every 10 seconds check if the session is stale
    const interval = setInterval(() => {
      if (isItTimeToShowSessionWarning() && !isModalOpen) {
        // if something is in full screen mode in presentation
        // do not show the modal and just extend the session
        if (
          document.fullscreenElement &&
          location.pathname.includes(BASE_ACTIVITIES_PRESENT)
        ) {
          extendSession();
        } else {
          isStaleSessionModalOpenVar(true);
        }
      }
    }, INTERVAL_IN_MILLISECONDS);

    return () => clearInterval(interval);
  }, [shouldTimerBeRunning, isModalOpen, location, extendSession]);

  return null;
};
